import React, {createContext, lazy, Suspense, useCallback, useContext, useEffect, useMemo, useRef, useState} from 'react';

/**
 * Транзиентные тосты (styleguide §Notifications): глобальный feedback по итогу
 * операции. role=status для нейтральных/успешных, role=alert для ошибок API,
 * чтобы скринридер озвучивал ошибку, не перехватывая фокус.
 *
 * Введён в CTHS-264: быстрые действия на карточке (носил/стирал/в корзину)
 * показывают ошибку API тостом, не ломая страницу. Опциональный action —
 * ссылка-кнопка в тосте («Перейти к вещи» после сохранения распознавания).
 *
 * Motion (2026-08-26). Тост приходит сверху и уходит туда же — обратный путь
 * совпадает с приходом, поэтому смахивание вверх ощущается очевидным. Отрисовка
 * живёт в ленивом `ToastViewport`: рантайм анимаций не должен лежать в главном
 * бандле ради экрана, где тостов не бывает (лендинг).
 */
export type ToastType = 'success' | 'error' | 'info';

/** Опциональное действие тоста — ссылка-кнопка («Перейти к вещи» и т.п.). */
export interface ToastAction {
    label: string;
    onClick: () => void;
}

export interface ToastItem {
    id: number;
    message: string;
    type: ToastType;
    action?: ToastAction;
}

interface ToastApi {
    showToast: (message: string, type?: ToastType, action?: ToastAction) => void;
}

const ToastContext = createContext<ToastApi | null>(null);

const AUTO_DISMISS_MS = 4000;

const importViewport = () => import('./ToastViewport');
const ToastViewport = lazy(importViewport);

/** Вне провайдера возвращает no-op — компонент остаётся монтируемым в изоляции (тесты). */
export const useToast = (): ToastApi => {
    const ctx = useContext(ToastContext);
    return ctx ?? {showToast: () => undefined};
};

interface ToastProviderProps {
    children: React.ReactNode;
    /**
     * Время жизни тоста. Вынесено в проп ради тестов: проверять таймеры на
     * реальных четырёх секундах — значит сделать тест медленным и хрупким, а
     * подменять таймеры нельзя, анимация выхода живёт на кадрах.
     */
    autoDismissMs?: number;
    /**
     * Подтягивать ли чанк слоя отрисовки заранее. На публичном лендинге и экранах
     * входа тостов не бывает — там рантайм анимаций скачивать незачем; в
     * приложении чанк должен быть на месте к первой же ошибке API.
     */
    prefetch?: boolean;
}

interface DismissTimer {
    /** `null`, пока таймер стоит на паузе (скрытая вкладка, курсор или фокус на тосте). */
    timeout: ReturnType<typeof setTimeout> | null;
    /** Момент, когда тост должен уйти; на паузе — сколько осталось. */
    expiresAt: number;
    remainingMs: number;
}

export const ToastProvider: React.FC<ToastProviderProps> = ({
    children,
    autoDismissMs = AUTO_DISMISS_MS,
    prefetch = true,
}) => {
    const [toasts, setToasts] = useState<ToastItem[]>([]);
    /**
     * Показав первый тост, слой отрисовки больше не размонтируем. Иначе при
     * закрытии последнего тоста исчезал бы сам AnimatePresence — и анимация
     * выхода не успевала бы проиграть ни разу.
     */
    const [viewportMounted, setViewportMounted] = useState(false);
    const idRef = useRef(0);
    /** Таймеры автозакрытия: их нужно уметь ставить на паузу и снимать. */
    const timers = useRef(new Map<number, DismissTimer>());

    const remove = useCallback((id: number) => {
        const timer = timers.current.get(id);
        if (timer?.timeout) clearTimeout(timer.timeout);
        timers.current.delete(id);
        setToasts((prev) => prev.filter((t) => t.id !== id));
    }, []);

    const scheduleDismiss = useCallback((id: number, delay: number) => {
        const timeout = setTimeout(() => remove(id), delay);
        timers.current.set(id, {timeout, expiresAt: Date.now() + delay, remainingMs: delay});
    }, [remove]);

    /** Останавливает таймер, запомнив остаток. Повторная пауза ничего не меняет. */
    const pause = useCallback((id: number) => {
        const timer = timers.current.get(id);
        if (!timer?.timeout) return;
        clearTimeout(timer.timeout);
        timers.current.set(id, {
            ...timer,
            timeout: null,
            remainingMs: Math.max(timer.expiresAt - Date.now(), 0),
        });
    }, []);

    /**
     * Возобновляет таймер с остатка, но не короче четверти срока: вернувшись к
     * тосту, пользователь должен успеть его дочитать.
     */
    const resume = useCallback((id: number) => {
        const timer = timers.current.get(id);
        if (!timer || timer.timeout) return;
        scheduleDismiss(id, Math.max(timer.remainingMs, autoDismissMs / 4));
    }, [autoDismissMs, scheduleDismiss]);

    const showToast = useCallback((message: string, type: ToastType = 'info', action?: ToastAction) => {
        idRef.current += 1;
        const id = idRef.current;
        setViewportMounted(true);
        setToasts((prev) => [...prev, {id, message, type, action}]);
        scheduleDismiss(id, autoDismissMs);
    }, [autoDismissMs, scheduleDismiss]);

    /**
     * В скрытой вкладке таймер встаёт на паузу: иначе сообщение сгорает, пока
     * пользователь смотрит в другое окно, и он о нём никогда не узнает.
     */
    useEffect(() => {
        const onVisibilityChange = () => {
            const ids = Array.from(timers.current.keys());
            if (document.visibilityState === 'hidden') {
                ids.forEach(pause);
            } else {
                ids.forEach(resume);
            }
        };

        document.addEventListener('visibilitychange', onVisibilityChange);
        return () => document.removeEventListener('visibilitychange', onVisibilityChange);
    }, [pause, resume]);

    /**
     * Чанк со слоем отрисовки подтягиваем после монтирования, вне критического
     * пути: он не удлиняет первый разбор бандла, и при этом к моменту первого
     * тоста уже загружен. Сообщение об ошибке API не должно ждать сетевого
     * запроса за анимацией. Там, где тостов не бывает (лендинг), не грузим
     * вовсе — первый `showToast` подтянет чанк сам.
     */
    useEffect(() => {
        if (prefetch) void importViewport();
    }, [prefetch]);

    // Таймеры не должны пережить провайдер.
    useEffect(() => {
        const pending = timers.current;
        return () => {
            pending.forEach(timer => {
                if (timer.timeout) clearTimeout(timer.timeout);
            });
            pending.clear();
        };
    }, []);

    const api = useMemo(() => ({showToast}), [showToast]);

    return (
        <ToastContext.Provider value={api}>
            {children}
            <div
                className="fixed top-4 left-1/2 -translate-x-1/2 z-[1400] flex flex-col gap-2 w-[calc(100%-2rem)] max-w-sm pointer-events-none"
                data-testid="toast-container"
            >
                {/* Чанк грузится вместе с первым тостом; до этого рисовать нечего. */}
                {viewportMounted && (
                    <Suspense fallback={null}>
                        <ToastViewport toasts={toasts} onDismiss={remove} onPause={pause} onResume={resume}/>
                    </Suspense>
                )}
            </div>
        </ToastContext.Provider>
    );
};

export default ToastProvider;
